import moment from 'moment';
import { WORKKARD_FIELD_KEY, MAILING_SAME_AS_ABOVE_FIELD_KEY, NON_TEMPLATE_FIELD_KEYS } from '../constants/fieldKeys';
import { stripInterTagNewlines } from '../../../../common/normalizers/richText';

const UNSET_CHOICE_VALUE = '-1';
const DATE_INPUT_FORMATS = ['MM/DD/YYYY', 'YYYY-MM-DD', moment.ISO_8601];
const WORKKARD_FILE_LOGICAL_NAME = 'WorkkardFnolFile';

function stripHashSuffix(dynamicKey) {
  if (typeof dynamicKey !== 'string') return dynamicKey;
  return dynamicKey.replace(/_[0-9a-f]{6,}$/i, '');
}

function extractTypeToken(dynamicKey) {
  const stable = stripHashSuffix(dynamicKey);
  const parts = String(stable).split('_');
  return parts.length >= 2 ? parts[parts.length - 1].toLowerCase() : '';
}

const DATE_ONLY_RE = /^(\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4})$/;

function toUnixSeconds(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1e12 ? Math.floor(value / 1000) : Math.floor(value);
  }
  if (value instanceof Date) return Math.floor(value.getTime() / 1000);
  
  if (typeof value === 'string' && DATE_ONLY_RE.test(value)) {
    const parsedUtc = moment.utc(value, DATE_INPUT_FORMATS, true);
    if (parsedUtc.isValid()) return parsedUtc.unix();
  }

  const parsed = moment(value, DATE_INPUT_FORMATS, true);
  if (parsed.isValid()) return parsed.unix();
  const loose = moment(value);
  return loose.isValid() ? loose.unix() : null;
}

function normalizeFileEntry(file, isEdit) {
  if (!file || typeof file !== 'object') return null;
  const guid = file.guid ?? file.FileGuid;
  if (!guid) return null;

  const caption = (file.caption ?? file.Caption ?? '').toString().trim();
  const entry = {
    guid,
    name: file.name ?? file.FileName ?? '',
    caption
  };
  if (isEdit) {
    entry.file_id = file.file_id ?? 0;
  }
  return entry;
}

function normalizeFileValue(value, isEdit) {
  if (!value) return [];
  const arr = Array.isArray(value) ? value : [value];
  return arr.map((file) => normalizeFileEntry(file, isEdit)).filter(Boolean);
}

function coerceValueByType(value, typeToken, isEdit) {
  if (typeToken === 'datetime' || typeToken === 'date') {
    return toUnixSeconds(value);
  }
  if (typeToken === 'singlechoicelist' || typeToken === 'multiplechoicelist') {
    if (value === UNSET_CHOICE_VALUE || value === '' || value === null || value === undefined) {
      return null;
    }
    return value;
  }
  if (typeToken === 'checkbox') {
    return value === true || value === 'true' || value === 1 ? 'Yes' : 'No';
  }
  if (typeToken === 'file') {
    return normalizeFileValue(value, isEdit);
  }
  return value === undefined ? null : value;
}

function buildFieldKeyToLogicName(fields) {
  const map = {}

  if(!Array.isArray(fields)) return map

  fields.forEach((f) => {
    const logicalName = f?.mapFieldKey
    if(!logicalName) return

    const candidates = [f?.fieldKey, f?.fieldName, f?.cf_field_name, f?.Name].filter(Boolean)
    candidates.forEach((k) => {map[k] = logicalName})
    candidates.forEach((k) => {
      const stable = stripHashSuffix(k)
      if(stable && !map[stable]) map[stable] = logicalName
    })
  })
  return map
}

function isFileFieldRow(row) {
  const type = String(row?.fieldType || row?.FieldType || '').toLowerCase();
  if (type === 'file') return true;
  const key = String(row?.fieldKey || row?.fieldName || row?.Name || '').toLowerCase();
  return /_file(_|$)/.test(key);
}

function buildFileFieldKeySet(fields) {
  const keys = new Set();

  if (!Array.isArray(fields)) return keys;

  fields.forEach((f) => {
    if (!isFileFieldRow(f)) return;

    [f?.fieldKey, f?.fieldName, f?.cf_field_name, f?.Name].filter(Boolean).forEach((k) => {
      keys.add(k);
      const stable = stripHashSuffix(k);
      if (stable) keys.add(stable);
    });
  });
  return keys;
}

function looksLikeFileValue(value) {
  const arr = Array.isArray(value) ? value : [value];
  return arr.length > 0 && arr.every((entry) => (
    entry && typeof entry === 'object' && (entry.guid !== undefined || entry.FileGuid !== undefined)
  ));
}

function toNumberOrNull(value) {
  if(value === undefined || value === null || value === "" || value === "-1") return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

export function buildWorkkardPayload({ formData, templateShortName, fields = [], isEdit = false }) {
  const templateFields = {};
  const unmappedKeys = [];

  const fieldKeyToLogicalName = buildFieldKeyToLogicName(fields)
  const fileFieldKeys = buildFileFieldKeySet(fields)

  Object.entries(formData).forEach(([key, rawValue]) => {
    if (NON_TEMPLATE_FIELD_KEYS.has(key)) return;
    if (key === MAILING_SAME_AS_ABOVE_FIELD_KEY) return;
    if(rawValue === undefined) return

    const stableKey = stripHashSuffix(key);
    const semanticName = fieldKeyToLogicalName[key] ?? fieldKeyToLogicalName[stableKey];
    const isFileField = extractTypeToken(key) === 'file'
      || fileFieldKeys.has(key)
      || fileFieldKeys.has(stableKey)
      || looksLikeFileValue(rawValue);
    const typeToken = isFileField ? 'file' : extractTypeToken(key);
    const coerced = coerceValueByType(rawValue, typeToken, isEdit);

    if (isFileField) {
      templateFields[WORKKARD_FILE_LOGICAL_NAME] = coerced;
      return;
    }

    if (!semanticName) {
      unmappedKeys.push(key);
      templateFields[key] = coerced;
      return;
    }
    templateFields[semanticName] = coerced;
  });

  if (unmappedKeys.length > 0) {
    console.warn('[buildWorkkardPayload] Unmapped keys passed through as-is:', unmappedKeys);
  } 

  const sponsorId = toNumberOrNull(formData[WORKKARD_FIELD_KEY.OWNER])

  const workkard = {
    workkard_short_name: templateShortName || formData.templateShortName || 'DEF',
    workkard_number: formData[WORKKARD_FIELD_KEY.NUMBER] || '',
    workkard_title: formData[WORKKARD_FIELD_KEY.TITLE] || '',
    carrier_id: normalizeCarrierId(formData[WORKKARD_FIELD_KEY.CARRIER]),
    workkard_sponsor_id: sponsorId,
    workkard_summary: stripInterTagNewlines(formData[WORKKARD_FIELD_KEY.DESCRIPTION] || ''),
    template_fields: templateFields,
  };

  return { workkard };
}

function normalizeCarrierId(value) {
  if(value === undefined || value === null || value === "" || value === "-1") return null
  return String(value)
}